import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

/** What is on the server now, as a 409 or the conflicts list reports it. */
export interface CurrentVersion {
  version: number;
  content: string;
  title?: string;
  status?: string;
  updatedBy: { id: string; name: string | null } | null;
  updatedAt: string;
}

interface ParkedEdit {
  id: string;
  chapterId: string;
  chapterTitle: string | null;
  sceneId: string | null;
  author: { id: string; name: string | null };
  proposed: { content: string | null; title?: string | null; status?: string | null };
  current?: CurrentVersion | null;
  resolution: 'kept' | 'applied' | null;
  resolvedAt: string | null;
}

/** A chapter's title and status, shown with its text: a clash can be in either. */
const Meta: React.FC<{ title?: string | null; status?: string | null }> = ({ title, status }) => {
  const { t } = useTranslation();
  if (!title && !status) return null;
  return (
    <div className="text-xs mb-1" style={{ color: 'var(--ds-text-secondary)' }}>
      {title ? `${t('stories.conflicts.titleLabel', 'Title')}: ${title}` : ''}
      {title && status ? ' · ' : ''}
      {status ? `${t('stories.conflicts.statusLabel', 'Status')}: ${status.replace('_', ' ')}` : ''}
    </div>
  );
};

const box = {
  background: 'var(--ds-bg-card)',
  border: '1px solid var(--ds-border)',
  color: 'var(--ds-text-primary)',
};

/**
 * Edits parked because they collided with someone else's change. The owner decides each
 * one; the editor who made it sees that it's waiting, then the outcome.
 */
export const PendingEdits: React.FC<{
  storyId: string;
  chapterId: string;
  isOwner: boolean;
  /** Unsaved text on the page: applying now would collide with it, so wait for a save. */
  dirty: boolean;
  /** Bumped by the editor after a save, so a freshly parked edit shows up. */
  reloadKey: number;
  partLabel: (sceneId: string | null) => string;
  onApplied: () => void;
}> = ({ storyId, chapterId, isOwner, dirty, reloadKey, partLabel, onApplied }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<ParkedEdit[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`/stories/${storyId}/conflicts`, { params: { chapterId } });
      setItems(res.data);
    } catch {
      // Not worth interrupting the writer over; the next save or visit tries again.
    }
  }, [storyId, chapterId]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const resolve = async (item: ParkedEdit, resolution: 'kept' | 'applied') => {
    setBusy(item.id);
    try {
      // Applying names the text the owner compared against; a newer change refuses it.
      await axios.post(`/stories/${storyId}/conflicts/${item.id}/resolve`, {
        resolution,
        ...(resolution === 'applied' && item.current
          ? { expectedVersion: item.current.version }
          : {}),
      });
      if (resolution === 'applied') onApplied();
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('stories.saveError', 'Failed to save'));
      load();
    } finally {
      setBusy(null);
    }
  };

  const dismiss = async (item: ParkedEdit) => {
    setItems((prev) => prev.filter((i) => i.id !== item.id));
    await axios.post(`/stories/${storyId}/conflicts/${item.id}/seen`).catch(() => undefined);
  };

  if (!items.length) return null;
  const who = (item: ParkedEdit) =>
    item.author.name || t('stories.members.unnamed', 'Unnamed writer');

  return (
    <div className="px-4 py-3 space-y-2 border-b" style={{ borderColor: 'var(--ds-border)' }}>
      {items.map((item) => (
        <div key={item.id} className="rounded-xl p-3 text-sm space-y-2" style={box}>
          <div className="flex items-start gap-2">
            <ExclamationTriangleIcon className="w-5 h-5 shrink-0 text-amber-500" />
            <div className="min-w-0">
              {isOwner
                ? t(
                    'stories.conflicts.waiting',
                    "{{name}}'s edit to {{part}} clashed with a newer change. Which should stay?",
                    { name: who(item), part: partLabel(item.sceneId) },
                  )
                : item.resolution === 'kept'
                  ? t(
                      'stories.conflicts.kept',
                      'The owner kept the current version of {{part}}, not your edit.',
                      { part: partLabel(item.sceneId) },
                    )
                  : item.resolution === 'applied'
                    ? t('stories.conflicts.applied', 'The owner used your version of {{part}}.', {
                        part: partLabel(item.sceneId),
                      })
                    : t(
                        'stories.conflicts.yours',
                        'Your edit to {{part}} clashed with a newer change. The owner will decide which stays.',
                        { part: partLabel(item.sceneId) },
                      )}
            </div>
          </div>
          {/* An editor can always get their own text back, even after "keep current". */}
          <button
            type="button"
            onClick={() => setOpen(open === item.id ? null : item.id)}
            className="text-xs text-primary-400 underline"
          >
            {open === item.id
              ? t('stories.conflicts.hide', 'Hide')
              : isOwner
                ? t('stories.conflicts.compare', 'Compare')
                : t('stories.conflicts.showMine', 'Show my version')}
          </button>
          {open === item.id && (
            <div className="grid gap-2 sm:grid-cols-2 [&>*]:min-w-0">
              {isOwner && (
                <div>
                  <div className="text-xs font-semibold mb-1">
                    {t('stories.conflicts.current', 'Current')}
                  </div>
                  {!item.sceneId && (
                    <Meta title={item.current?.title} status={item.current?.status} />
                  )}
                  <div
                    className="max-h-48 overflow-y-auto whitespace-pre-wrap break-words text-xs rounded-lg p-2"
                    style={{ border: '1px solid var(--ds-border)' }}
                  >
                    {item.current?.content || '—'}
                  </div>
                </div>
              )}
              <div>
                <div className="text-xs font-semibold mb-1">
                  {isOwner
                    ? t('stories.conflicts.theirs', "{{name}}'s version", { name: who(item) })
                    : t('stories.conflicts.mine', 'Your version')}
                </div>
                {!item.sceneId && (
                  <Meta title={item.proposed.title} status={item.proposed.status} />
                )}
                <div
                  className="max-h-48 overflow-y-auto whitespace-pre-wrap break-words text-xs rounded-lg p-2"
                  style={{ border: '1px solid var(--ds-border)' }}
                >
                  {item.proposed.content || '—'}
                </div>
              </div>
            </div>
          )}
          {isOwner ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={busy === item.id}
                onClick={() => resolve(item, 'kept')}
                className="px-3 py-1.5 rounded-lg border text-xs font-medium disabled:opacity-50"
                style={{ borderColor: 'var(--ds-border)' }}
              >
                {t('stories.conflicts.keepCurrent', 'Keep current')}
              </button>
              <button
                type="button"
                disabled={busy === item.id || dirty}
                title={
                  dirty
                    ? t('stories.conflicts.saveFirst', 'Save your own changes first')
                    : undefined
                }
                onClick={() => resolve(item, 'applied')}
                className="px-3 py-1.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-xs font-medium disabled:opacity-50"
              >
                {t('stories.conflicts.useTheirs', "Use {{name}}'s version", { name: who(item) })}
              </button>
              {dirty && (
                <span className="text-xs self-center" style={{ color: 'var(--ds-text-secondary)' }}>
                  {t('stories.conflicts.saveFirst', 'Save your own changes first')}
                </span>
              )}
            </div>
          ) : (
            item.resolution && (
              <button
                type="button"
                onClick={() => dismiss(item)}
                className="text-xs"
                style={{ color: 'var(--ds-text-secondary)' }}
              >
                {t('stories.conflicts.dismiss', 'OK')}
              </button>
            )
          )}
        </div>
      ))}
    </div>
  );
};

/** The owner's own save hit a newer change: keep theirs or take what's there now. */
export const SaveConflictDialog: React.FC<{
  part: string;
  current: CurrentVersion;
  currentUserId?: string;
  onKeepMine: () => void;
  onTakeTheirs: () => void;
}> = ({ part, current, currentUserId, onKeepMine, onTakeTheirs }) => {
  const { t } = useTranslation();
  const name = current.updatedBy?.name || t('stories.conflicts.someone', 'Someone');
  // Who changed it decides how to say it: nobody recorded means an AI change or a restore.
  const heading = !current.updatedBy
    ? t(
        'stories.conflicts.dialogTitleUnknown',
        '{{part}} changed since you opened it (an AI change or a restore)',
        { part },
      )
    : current.updatedBy.id === currentUserId
      ? t('stories.conflicts.dialogTitleSelf', 'You changed {{part}} in another tab or device', {
          part,
        })
      : t('stories.conflicts.dialogTitle', '{{name}} changed {{part}} since you opened it', {
          name,
          part,
        });
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4">
      <div
        role="dialog"
        aria-labelledby="conflict-title"
        className="w-full max-w-lg rounded-2xl p-5 space-y-3"
        style={{ background: 'var(--ds-surface, #fff)', color: 'var(--ds-text-primary)' }}
      >
        <h3 id="conflict-title" className="text-base font-semibold">
          {heading}
        </h3>
        <div className="text-xs font-semibold" style={{ color: 'var(--ds-text-secondary)' }}>
          {t('stories.conflicts.theirCurrent', 'What is there now')}
        </div>
        <Meta title={current.title} status={current.status} />
        <div
          className="max-h-60 overflow-y-auto whitespace-pre-wrap break-words text-sm rounded-lg p-3"
          style={{ border: '1px solid var(--ds-border)' }}
        >
          {current.content || '—'}
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onTakeTheirs}
            className="px-4 py-2 rounded-lg border text-sm font-medium"
            style={{ borderColor: 'var(--ds-border)' }}
          >
            {t('stories.conflicts.takeTheirs', 'Use what is there now')}
          </button>
          <button
            type="button"
            onClick={onKeepMine}
            className="px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium"
          >
            {t('stories.conflicts.keepMine', 'Keep mine')}
          </button>
        </div>
      </div>
    </div>
  );
};

/** The workspace's pointer to edits waiting on the owner (or, for an editor, to news). */
export const PendingEditsSummary: React.FC<{
  storyId: string;
  isOwner: boolean;
  onOpen: (chapterId: string) => void;
}> = ({ storyId, isOwner, onOpen }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<ParkedEdit[]>([]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`/stories/${storyId}/conflicts`)
      .then((res) => !cancelled && setItems(res.data))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [storyId]);

  if (!items.length) return null;
  const chapters = [...new Map(items.map((i) => [i.chapterId, i.chapterTitle])).entries()];
  return (
    <div className="rounded-2xl p-4 space-y-2 text-sm" style={box}>
      <div className="flex items-center gap-2 font-semibold">
        <ExclamationTriangleIcon className="w-5 h-5 text-amber-500" />
        {isOwner
          ? t('stories.conflicts.summaryOwner', '{{count}} edits are waiting for your decision', {
              count: items.length,
            })
          : t('stories.conflicts.summaryEditor', 'News about your edits')}
      </div>
      <div className="flex flex-wrap gap-2">
        {chapters.map(([chapterId, chapterTitle]) => (
          <button
            key={chapterId}
            type="button"
            onClick={() => onOpen(chapterId)}
            className="px-3 py-1 rounded-lg border text-xs text-primary-400"
            style={{ borderColor: 'var(--ds-border)' }}
          >
            {chapterTitle || t('stories.conflicts.untitled', 'Untitled')}
          </button>
        ))}
      </div>
    </div>
  );
};
